import React, { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { loanApplicationSignalRController } from 'app/services/signalR';

import { FeatureFlag } from 'constants/featureFlags';
import { PresenceEntityType } from 'constants/presence';
import useLoanId from 'hooks/useLoanId';
import usePresence from 'hooks/usePresence';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { setIsSignalRInitiatedAction } from 'store/loanApplications/actions';

import FollowModeBanner from '../FollowModeBanner';
import { usePresenceLocationUpdate, useStopFollowOnManualNavigation } from '../hooks';
import PresenceToast from '../PresenceToast';

const LoanApplicationSignalRContainer = ({ children }) => {
  usePresenceLocationUpdate();
  useStopFollowOnManualNavigation();

  const loanId = useLoanId();

  const { applicationId } = useParams();

  const companyId = useSelector(selectMasterCompanyId);

  const dispatch = useDispatch();

  const { getState } = useStore();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  // Must be wired between connect and join: the loan-editor listeners (checklists, AUS, tasks,
  // field locking, follow mode) share the connection that presence joins on.
  const onConnectionReady = useCallback(() => {
    loanApplicationSignalRController.init(dispatch, { isExternalLoanLockEnabled }, getState);
    loanApplicationSignalRController.onPageOpened(loanId, applicationId, companyId);
  }, [applicationId, companyId, dispatch, getState, isExternalLoanLockEnabled, loanId]);

  const onJoined = useCallback(() => {
    dispatch(setIsSignalRInitiatedAction(true));
  }, [dispatch]);

  const onLeaving = useCallback(
    () => loanApplicationSignalRController.onPageClosed(loanId, companyId),
    [companyId, loanId],
  );

  const onLeft = useCallback(() => {
    dispatch(setIsSignalRInitiatedAction(false));
  }, [dispatch]);

  usePresence({
    entityType: PresenceEntityType.LOAN,
    entityId: loanId,
    // onConnectionReady reads these when it inits the controller, and it is held in a ref, so
    // they have to reach the connect effect here or a late flag flip leaves the controller
    // wired with the stale value.
    reinitKey: `${isExternalLoanLockEnabled}:${applicationId}`,
    onConnectionReady,
    onJoined,
    onLeaving,
    onLeft,
  });

  return (
    <>
      <FollowModeBanner />
      {children}
      <PresenceToast />
    </>
  );
};

export default LoanApplicationSignalRContainer;
